import React from 'react';
import { useIntl } from 'react-intl';
import type { MusicVideoEditorResponse } from 'fym-shared/src/music24/musicVideoApi';
import type { MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import { MUSIC_VIDEO_SIZE_PX } from 'fym-shared/src/music24/musicVideoLook';
import { TypedMemo } from '../../common/types';
import { ConfirmDialog } from '../../common/views/modals/confirmDialog/ConfirmDialog';
import type { MakeVideoError } from '../doc/musicVideoErrors';
import type { CutSaveState } from '../doc/useCutAutosave';
import { SAVE_STATE_MESSAGES } from '../topbar/saveStateMessages';
import { stateMessages } from '../messages/stateMessages';
import { formatTime } from '../timeline/timelineGeometry';
import { WatermarkCallout } from '../../music24/watermarkUpsell/files/WatermarkCallout';
import { watermarkFileMessages } from '../../music24/watermarkUpsell/files/watermarkFileMessages';
import { useWatermarkChoice } from '../../music24/watermarkUpsell/choice/useWatermarkChoice';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ERROR_MESSAGES = {
  footageGone: stateMessages.makeErrorFootageGone,
  footageProcessing: stateMessages.makeErrorFootageProcessing,
  releases: stateMessages.makeErrorReleases,
  noMaster: stateMessages.makeErrorNoMaster,
  demo: stateMessages.makeErrorDemo,
  failed: stateMessages.makeErrorFailed,
  plan: stateMessages.makeErrorFailed,
  credits: stateMessages.makeErrorFailed,
} as const;

const bold = (chunks: React.ReactNode[]) => <b>{chunks}</b>;

interface MakeDialogProps {
  isOpen: boolean;
  data: MusicVideoEditorResponse;
  doc: MusicVideoDoc;
  cutNumber: number;
  isBusy: boolean;
  error: MakeVideoError | null;
  /** The cut did not save before the make: the save state (nothing was sent, no credit is charged). */
  saveError: CutSaveState | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * "Make the music video" (design e2): the length, the size and the cost of the open cut, and a note
 * when the song already has an added music video (an upload or a YouTube link) that the new one
 * replaces in the Kit. On Free and the trial the video is a watermarked preview: the callout says
 * so before the credits are spent, and the button reads "Make the preview" (design: trial-upsell
 * a6). The showcase never opens this dialog (`ShowcaseEditorFrame`).
 */
const MakeDialogComponent: React.FC<MakeDialogProps> = ({
  isOpen,
  data,
  doc,
  cutNumber,
  isBusy,
  error,
  saveError,
  onConfirm,
  onCancel,
}) => {
  const intl = useIntl();
  const credits = data.plan.renderCredits;
  const left = data.plan.creditsLeft;
  const size = MUSIC_VIDEO_SIZE_PX;
  const length = formatTime(doc.settings.endSec - doc.settings.startSec);
  const isFull =
    doc.settings.startSec === 0 &&
    Math.abs(doc.settings.endSec - (data.song.durationSec ?? doc.settings.endSec)) < 0.01;
  const source = data.musicVideo?.source;
  const { applies: isPreviewPlan } = useWatermarkChoice();
  let errorText: string | undefined;
  if (saveError !== null) {
    errorText = intl.formatMessage(SAVE_STATE_MESSAGES[saveError]);
  } else if (error !== null) {
    errorText = intl.formatMessage(ERROR_MESSAGES[error]);
  }

  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="neutral"
      title={intl.formatMessage(stateMessages.makeTitle)}
      description={intl.formatMessage(stateMessages.makeText, { number: cutNumber })}
      confirmLabel={
        isPreviewPlan
          ? intl.formatMessage(watermarkFileMessages.makePreview)
          : intl.formatMessage(stateMessages.makeConfirm, { credits })
      }
      isBusy={isBusy}
      error={errorText}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <div className={styles.facts}>
        <span>{intl.formatMessage(stateMessages.factLength)}</span>
        <b>{isFull ? intl.formatMessage(stateMessages.factLengthFull, { length }) : length}</b>
        <span>{intl.formatMessage(stateMessages.factSize)}</span>
        <b>{intl.formatMessage(stateMessages.factSizeValue, { width: size.width, height: size.height })}</b>
        <span>{intl.formatMessage(stateMessages.factCost)}</span>
        <b>
          {left === null
            ? intl.formatMessage(stateMessages.factCostPlain, { credits })
            : intl.formatMessage(stateMessages.factCostValue, { credits, before: left, after: left - credits })}
        </b>
      </div>
      <WatermarkCallout variant="make" surface="make_dialog" credits={credits} className="mt-3" />
      {(source === 'upload' || source === 'youtube') && (
        <p className={styles.alertInfo}>
          {intl.formatMessage(source === 'upload' ? stateMessages.replaceUpload : stateMessages.replaceYouTube, {
            b: bold,
          })}
        </p>
      )}
    </ConfirmDialog>
  );
};

export const MakeDialog = TypedMemo(MakeDialogComponent);
